import React, { useCallback, useMemo, useState } from "react";
import { AutocompleteInput } from "./AutocompleteInput";
import { useAuth } from "../auth/AuthContext";
import { PageState } from "./PageState";
import { usePagedQuery } from "../hooks/usePagedQuery";
import { CrudModal } from "./CrudModal";
import { ConfirmDialog } from "./ConfirmDialog";

type Option = { id: number; label: string };

type BaseFieldConfig<T> = {
  name: keyof T & string;
  label: string;
  required?: boolean;
  disabled?:
    | boolean
    | ((context: {
        values: Record<string, string | number | boolean | null>;
        editingId: number | null;
        readOnly: boolean;
      }) => boolean);
  displayValue?: (
    value: string | number | boolean | null,
    values: Record<string, string | number | boolean | null>
  ) => React.ReactNode;
};

type TextFieldConfig<T> = BaseFieldConfig<T> & {
  type: "text" | "email" | "tel" | "date" | "number";
  placeholder?: string;
};

type SelectFieldConfig<T> = BaseFieldConfig<T> & {
  type: "select";
  options?: { value: string; label: string }[];
  fetchOptions?: (query: string) => Promise<Option[]>;
};

type CheckboxFieldConfig<T> = BaseFieldConfig<T> & {
  type: "checkbox";
};

type AutocompleteFieldConfig<T> = BaseFieldConfig<T> & {
  type: "autocomplete";
  placeholder?: string;
  fetchOptions: (query: string) => Promise<Option[]>;
  getIdFromItem: (item: T) => number | null;
  getLabelFromItem: (item: T) => string;
};

type FieldConfig<T> =
  | TextFieldConfig<T>
  | SelectFieldConfig<T>
  | CheckboxFieldConfig<T>
  | AutocompleteFieldConfig<T>;

type CrudPageProps<T> = {
  title: string;
  endpoint: string;
  searchPlaceholder: string;
  columns: { label: string; render: (item: T) => React.ReactNode }[];
  formFields: FieldConfig<T>[];
  initialValues: Record<string, string | number | boolean | null>;
  mapToPayload: (values: Record<string, string | number | boolean | null>) => Record<string, unknown>;
  renderCreateHint?: React.ReactNode;
  permissionRead?: string;
  permissionWrite?: string;
  permissionCreate?: string;
  permissionEdit?: string;
  permissionDelete?: string;
  canWrite?: boolean;
  customActions?: (item: T, refresh: () => Promise<void>) => React.ReactNode;
  listFilters?: Record<string, unknown>;
  renderHeaderActions?: (context: {
    rows: T[];
    refresh: () => Promise<void>;
    canWrite: boolean;
    query: Record<string, unknown>;
    loading: boolean;
    disabled: boolean;
  }) => React.ReactNode;
  renderViewContent?: (context: {
    item: T | null;
    values: Record<string, string | number | boolean | null>;
  }) => React.ReactNode;
  renderFormExtras?: (context: {
    values: Record<string, string | number | boolean | null>;
    setValues: React.Dispatch<React.SetStateAction<Record<string, string | number | boolean | null>>>;
    setAutocompleteLabel: (fieldName: string, label: string) => void;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    editingId: number | null;
    readOnly: boolean;
    refresh: () => Promise<void>;
  }) => React.ReactNode;
  pauseQuery?: boolean;
  disableInteractions?: boolean;
  lockInteractionsWhileLoading?: boolean;
  renderFilters?:
    | React.ReactNode
    | ((context: {
        loading: boolean;
        disabled: boolean;
        refresh: () => Promise<void>;
      }) => React.ReactNode);
};

export const CrudPage = <T extends { id: number }>({
  title,
  endpoint,
  searchPlaceholder,
  columns,
  formFields,
  initialValues,
  mapToPayload,
  renderCreateHint,
  permissionRead,
  permissionWrite,
  permissionCreate,
  permissionEdit,
  permissionDelete,
  canWrite: canWriteOverride,
  customActions,
  listFilters,
  renderFilters,
  renderHeaderActions,
  renderViewContent,
  renderFormExtras,
  pauseQuery = false,
  disableInteractions = false,
  lockInteractionsWhileLoading = false,
}: CrudPageProps<T>) => {
  const { client, user } = useAuth();
  
  // Verificar permissão de leitura
  const canRead = useMemo(() => {
    if (permissionRead) {
      return Boolean(user?.permissions?.includes(permissionRead));
    }
    return true;
  }, [permissionRead, user?.permissions]);

  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [autocompleteLabels, setAutocompleteLabels] = useState<Record<string, string>>(
    {}
  );
  const [selectOptions, setSelectOptions] = useState<Record<string, Option[]>>({});

  // Load select options on mount
  React.useEffect(() => {
    const loadSelectOptions = async () => {
      for (const field of formFields) {
        if (field.type === "select" && field.fetchOptions) {
          const options = await field.fetchOptions("");
          setSelectOptions((prev) => ({
            ...prev,
            [field.name]: options,
          }));
        }
      }
    };
    loadSelectOptions();
  }, [formFields]);
  const resolveEntityId = (value: unknown) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return null;
    }
    return value;
  };

  const resetForm = useCallback(() => {
    setEditingId(null);
    setSelectedItem(null);
    setFormValues(initialValues);
    setAutocompleteLabels({});
  }, [initialValues]);

  const canWrite = useMemo(() => {
    if (typeof canWriteOverride === "boolean") {
      return canWriteOverride;
    }
    if (permissionWrite) {
      return Boolean(user?.permissions?.includes(permissionWrite));
    }
    return true;
  }, [canWriteOverride, permissionWrite, user?.permissions]);

  const canCreate = useMemo(() => {
    if (permissionCreate) {
      return Boolean(user?.permissions?.includes(permissionCreate));
    }
    return canWrite;
  }, [canWrite, permissionCreate, user?.permissions]);

  const canEdit = useMemo(() => {
    if (permissionEdit) {
      return Boolean(user?.permissions?.includes(permissionEdit));
    }
    return canWrite;
  }, [canWrite, permissionEdit, user?.permissions]);

  const canDelete = useMemo(() => {
    if (permissionDelete) {
      return Boolean(user?.permissions?.includes(permissionDelete));
    }
    return canWrite;
  }, [canWrite, permissionDelete, user?.permissions]);

    const entityId = resolveEntityId(formValues.entityId);
  const combinedFilters = useMemo(
    () => ({
      ...(entityId ? { entityId } : {}),
      ...(listFilters ?? {}),
    }),
    [entityId, listFilters]
  );
  const {
    rows,
    total,
    loading,
    error: listError,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<T>({
    endpoint,
    filters: combinedFilters,
    enabled: canRead,
    pause: pauseQuery,
  });
  const exportQuery = useMemo(() => ({ q, ...combinedFilters }), [q, combinedFilters]);
  const interactionsDisabled = disableInteractions || (lockInteractionsWhileLoading && loading);

  const openModal = useCallback(
    (mode: "create" | "edit" | "view", item?: T) => {
      if (mode === "create") {
        resetForm();
        setModalMode("create");
        setModalOpen(true);
        return;
      }
      if (item) {
        setSelectedItem(item);
        setEditingId(item.id);
        const updatedValues: Record<string, string | number | boolean | null> = {
          ...initialValues,
        };
        const labels: Record<string, string> = {};
        for (const field of formFields) {
          if (field.type === "autocomplete") {
            const id = field.getIdFromItem(item);
            updatedValues[field.name] = id ?? null;
            labels[field.name] = field.getLabelFromItem(item);
          } else {
            const value = (item as Record<string, unknown>)[field.name];
            updatedValues[field.name] = (value as string | number | boolean | null) ?? null;
          }
        }
        setFormValues(updatedValues);
        setAutocompleteLabels(labels);
      }
      setModalMode(mode);
      setModalOpen(true);
    },
    [formFields, initialValues, resetForm]
  );

  const closeModal = () => {
    setModalOpen(false);
    if (modalMode !== "edit") {
      resetForm();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!canWrite || saving) {
      return;
    }
    const requiredField = formFields.find((field) => {
      if (!field.required) {
        return false;
      }
      const value = formValues[field.name];
      if (field.type === "checkbox") {
        return false;
      }
      return value === null || value === undefined || value === "";
    });
    if (requiredField) {
      setError(`Campo ${requiredField.label} e obrigatorio.`);
      return;
    }
    try {
      let payload: Record<string, unknown>;
      try {
        payload = mapToPayload(formValues);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Falha ao salvar.");
        return;
      }
      setSaving(true);
      const entityId = resolveEntityId(
        (payload as Record<string, unknown>).entityId
      );
      const entityQuery = entityId ? `?entityId=${entityId}` : "";
      const response = editingId
        ? await client.put(`${endpoint}/${editingId}${entityQuery}`, payload)
        : await client.post(`${endpoint}${entityQuery}`, payload);

      if (!response.ok) {
        setError("Falha ao salvar.");
        return;
      }

      resetForm();
      setModalOpen(false);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) {
      return;
    }
    setError(null);
    const response = await client.del(`${endpoint}/${deleteId}`);
    if (!response.ok) {
      setError("Falha ao remover.");
      return;
    }
    setConfirmOpen(false);
    setDeleteId(null);
    await reload();
  };

  const paginationText = useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  const isReadOnly =
    modalMode === "view" ||
    (modalMode === "edit" && !canEdit) ||
    (modalMode === "create" && !canCreate);

  if (!canRead) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-shield-exclamation text-warning" style={{ fontSize: "4rem" }}></i>
                </div>
                <h2 className="mb-3">Acesso Negado</h2>
                <p className="text-muted mb-4">
                  VocG nuo tem permissuo para acessar <strong>{title}</strong>.
                </p>
                <p className="text-muted small">
                  Entre em contato com o administrador do sistema para solicitar acesso.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <h1 className="mb-0">{title}</h1>
        <div className="d-flex flex-wrap gap-2">
          {renderHeaderActions?.({
            rows,
            refresh: reload,
            canWrite,
            query: exportQuery,
            loading,
            disabled: interactionsDisabled,
          })}
          {canCreate && (
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => openModal("create")}
              disabled={interactionsDisabled}
            >
              Novo
            </button>
          )}
        </div>
      </div>
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex gap-2 mb-3">
            <input
              className="form-control"
              placeholder={searchPlaceholder}
              value={q}
              onChange={(event) => setQ(event.target.value)}
              disabled={interactionsDisabled}
            />
            <button
              className="btn btn-outline-secondary"
              onClick={() => setQ("")}
              type="button"
              disabled={interactionsDisabled}
            >
              Limpar
            </button>
            {loading && (
              <span className="input-group-text bg-transparent border-0">
                <span className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Carregando...
              </span>
            )}
          </div>
          {renderFilters && (
            <div className="mb-3">
              {typeof renderFilters === "function"
                ? renderFilters({
                    loading,
                    disabled: interactionsDisabled,
                    refresh: reload,
                  })
                : renderFilters}
            </div>
          )}
          {error && <div className="alert alert-danger">{error}</div>}
          <PageState loading={loading} error={listError}>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column.label}>{column.label}</th>
                    ))}
                    <th>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item.id}>
                      {columns.map((column) => (
                        <td key={column.label}>{column.render(item)}</td>
                      ))}
                      <td className="text-end">
                        {customActions && (
                          <span
                            className="d-inline-flex"
                            style={interactionsDisabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
                          >
                            {customActions(item, reload)}
                          </span>
                        )}
                        <button
                          className="btn btn-sm btn-outline-secondary me-2"
                          onClick={() => openModal("view", item)}
                          disabled={interactionsDisabled}
                        >
                          Ver
                        </button>
                        {canEdit || canDelete ? (
                          <>
                            {canEdit && (
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => openModal("edit", item)}
                                disabled={interactionsDisabled}
                              >
                                Editar
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  setDeleteId(item.id);
                                  setConfirmOpen(true);
                                }}
                                disabled={interactionsDisabled}
                              >
                                Excluir
                              </button>
                            )}
                          </>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openModal("view", item)}
                            disabled={interactionsDisabled}
                          >
                            Visualizar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="text-center">
                        Nenhum registro encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PageState>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">{paginationText}</small>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={interactionsDisabled || page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={interactionsDisabled || page * take >= total}
                onClick={() => setPage(page + 1)}
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>
      <CrudModal
        title={
          modalMode === "create"
            ? "Novo"
            : modalMode === "edit"
              ? "Editar"
              : "Detalhes"
        }
        open={modalOpen}
        mode={modalMode}
        canWrite={canWrite}
        saving={saving}
        onClose={closeModal}
        formId="crud-form"
      >
        {modalMode === "view" ? (
          renderViewContent ? (
            renderViewContent({ item: selectedItem, values: formValues })
          ) : (
            <div className="row g-3">
              {formFields.map((field) => {
                const value = formValues[field.name];
                let displayValue = value ?? "-";
                if (field.displayValue) {
                  displayValue = field.displayValue(value, formValues);
                  return (
                    <div className="col-12 col-md-6" key={field.name}>
                      <div className="text-muted small">{field.label}</div>
                      <div className="fw-semibold">{displayValue}</div>
                    </div>
                  );
                }
                if (field.type === "checkbox") {
                  displayValue = value ? "Sim" : "Nao";
                }
                if (field.type === "autocomplete") {
                  displayValue = autocompleteLabels[field.name] || "-";
                }
                if (field.type === "select") {
                  const options = field.fetchOptions
                    ? selectOptions[field.name] || []
                    : field.options || [];
                  const found = options.find(
                    (option) =>
                      String(option.id || option.value) === String(value ?? "")
                  );
                  displayValue = found?.label ?? "-";
                }
                return (
                  <div className="col-12 col-md-6" key={field.name}>
                    <div className="text-muted small">{field.label}</div>
                    <div className="fw-semibold">{displayValue}</div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <form onSubmit={handleSubmit} id="crud-form">
            {modalMode === "create" && renderCreateHint}
            {formFields.map((field) => {
              const fieldDisabled =
                isReadOnly ||
                (typeof field.disabled === "function"
                  ? field.disabled({
                      values: formValues,
                      editingId,
                      readOnly: isReadOnly,
                    })
                  : Boolean(field.disabled));
              if (field.type === "checkbox") {
                return (
                  <div className="form-check mb-3" key={field.name}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={Boolean(formValues[field.name])}
                      disabled={fieldDisabled}
                      onChange={(event) =>
                        setFormValues((prev) => ({
                          ...prev,
                          [field.name]: event.target.checked,
                        }))
                      }
                    />
                    <label className="form-check-label">{field.label}</label>
                  </div>
                );
              }

              if (field.type === "select") {
                const options = field.fetchOptions
                  ? selectOptions[field.name] || []
                  : field.options || [];

                return (
                  <div className="mb-3" key={field.name}>
                    <label className="form-label">
                      {field.label}
                      {field.required && <span className="text-danger"> *</span>}
                    </label>
                    <select
                      className="form-select"
                      value={String(formValues[field.name] ?? "")}
                      disabled={fieldDisabled}
                      required={field.required && !fieldDisabled}
                      onChange={(event) =>
                        setFormValues((prev) => ({
                          ...prev,
                          [field.name]: event.target.value,
                        }))
                      }
                    >
                      <option value="">Selecione...</option>
                      {options.map((option) => (
                        <option
                          key={option.id || option.value}
                          value={option.id || option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (field.type === "autocomplete") {
                return (
                  <AutocompleteInput
                    key={field.name}
                    label={field.label}
                    placeholder={field.placeholder}
                    value={(formValues[field.name] as number | null) ?? null}
                    selectedLabel={autocompleteLabels[field.name]}
                    fetchOptions={field.fetchOptions}
                    disabled={fieldDisabled}
                    onSelect={(option) => {
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: option?.id ?? null,
                      }));
                      setAutocompleteLabels((prev) => ({
                        ...prev,
                        [field.name]: option?.label ?? "",
                      }));
                    }}
                  />
                );
              }

              return (
                <div className="mb-3" key={field.name}>
                  <label className="form-label">
                    {field.label}
                    {field.required && <span className="text-danger"> *</span>}
                  </label>
                  <input
                    className="form-control"
                    type={field.type}
                    placeholder={field.placeholder}
                    value={(formValues[field.name] as string | number | null) ?? ""}
                    disabled={fieldDisabled}
                    onChange={(event) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: event.target.value,
                      }))
                    }
                    required={field.required && !fieldDisabled}
                    {...(field.type === "number" && {
                      step: "any",
                    })}
                  />
                </div>
              );
            })}
            {renderFormExtras?.({
              values: formValues,
              setValues: setFormValues,
              setAutocompleteLabel: (fieldName, label) =>
                setAutocompleteLabels((prev) => ({
                  ...prev,
                  [fieldName]: label,
                })),
              setError,
              editingId,
              readOnly: isReadOnly,
              refresh: reload,
            })}
            {error && <div className="alert alert-danger">{error}</div>}
          </form>
        )}
      </CrudModal>
      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar exclusao"
        description="Deseja remover este registro?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        danger
        onCancel={() => {
          setConfirmOpen(false);
          setDeleteId(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
};


